const LOCAL_STORAGE_KEY = "elite_ai_calling_auth_session";
const SESSION_STORAGE_KEY = "elite_ai_calling_auth_temp_session";
const AUTH_NOTICE_KEY = "elite_ai_calling_auth_notice";
const AUTH_STATE_EVENT = "elite-auth-state-change";

function canUseStorage() {
  return typeof window !== "undefined";
}

function parseStoredValue(rawValue) {
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    return null;
  }
}

function getStorageSnapshot() {
  if (!canUseStorage()) {
    return null;
  }

  return (
    parseStoredValue(window.localStorage.getItem(LOCAL_STORAGE_KEY)) ||
    parseStoredValue(window.sessionStorage.getItem(SESSION_STORAGE_KEY))
  );
}

function dispatchAuthStateChange() {
  if (!canUseStorage()) {
    return;
  }

  window.dispatchEvent(new CustomEvent(AUTH_STATE_EVENT));
}

function clearStoredSessions() {
  if (!canUseStorage()) {
    return;
  }

  window.localStorage.removeItem(LOCAL_STORAGE_KEY);
  window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
}

export function getAuthStateEventName() {
  return AUTH_STATE_EVENT;
}

export function getStoredAuthSession() {
  return getStorageSnapshot();
}

export function isStoredSessionPersistent() {
  if (!canUseStorage()) {
    return false;
  }

  return Boolean(window.localStorage.getItem(LOCAL_STORAGE_KEY));
}

export function getStoredUser() {
  return getStorageSnapshot()?.user || null;
}

export function getStoredToken() {
  const session = getStorageSnapshot();

  return String(session?.token || "").trim();
}

export function hasStoredSession() {
  return Boolean(getStoredToken());
}

export function persistAuthSession(session, rememberMe = false) {
  if (!canUseStorage()) {
    return session;
  }

  clearStoredSessions();

  const normalizedSession = {
    token: String(session?.token || "").trim(),
    tokenType: session?.tokenType || "Bearer",
    expiresIn: session?.expiresIn || null,
    user: session?.user || null,
    storedAt: new Date().toISOString(),
  };

  const targetStorage = rememberMe
    ? window.localStorage
    : window.sessionStorage;
  const storageKey = rememberMe ? LOCAL_STORAGE_KEY : SESSION_STORAGE_KEY;

  targetStorage.setItem(storageKey, JSON.stringify(normalizedSession));
  dispatchAuthStateChange();

  return normalizedSession;
}

export function clearAuthSession() {
  clearStoredSessions();
  dispatchAuthStateChange();
}

export function setAuthNotice(message) {
  if (!canUseStorage()) {
    return;
  }

  const text = String(message || "").trim();

  if (!text) {
    window.sessionStorage.removeItem(AUTH_NOTICE_KEY);
    return;
  }

  window.sessionStorage.setItem(AUTH_NOTICE_KEY, text);
}

export function consumeAuthNotice() {
  if (!canUseStorage()) {
    return "";
  }

  const message = window.sessionStorage.getItem(AUTH_NOTICE_KEY) || "";
  window.sessionStorage.removeItem(AUTH_NOTICE_KEY);
  return message;
}
